'use client';

import { FC } from 'react';
import { Content } from '@prismicio/client';
import { JSXMapSerializer, PrismicRichText } from '@prismicio/react';

import { Button, Container, Video } from '@/components';

import styles from './defaultStyles.module.scss';
import { GetOfferAdvantages, GetOfferProducts, GetOfferSafety } from '.';
import { useMounted, useScreenSize, useVisible } from '@/hooks';
import { screenSizes } from '@/constants';

type Props = {
  slice: Content.GetOfferSlice;
};

const components = (
  componentsStyles: {
    [key: string]: string;
  },
  variation?: string
): JSXMapSerializer => ({
  heading2: ({ children }) => (
    <h2
      className={`${componentsStyles.title} ${variation ? componentsStyles[variation] : ''}`}
    >
      {children}
    </h2>
  ),
  paragraph: ({ children }) => (
    <p
      className={`${componentsStyles.title} ${variation ? componentsStyles[variation] : ''}`}
    >
      {children}
    </p>
  ),
});

export const GetOfferInner: FC<Props> = ({ slice }) => {
  const size = useScreenSize();
  const isMounted = useMounted();
  const [isVisible, id] = useVisible();

  const isVisibleStyles = isVisible ? styles.isVisible : '';
  const onlyProducts =
    slice.variation === 'onlyProducts' ||
    slice.variation === 'productsWithButton';

  const isDesktop = size.width > screenSizes.tablet && isMounted;
  const isMobile = size.width <= screenSizes.tablet && isMounted;

  return (
    <div className={styles.inner} id={id}>
      {onlyProducts ? (
        <div className={styles.productsWrapper}>
          <div className={`${styles.title_wrapper} ${isVisibleStyles} `}>
            <PrismicRichText
              field={slice.primary.title}
              components={components(styles, slice.variation)}
            />
          </div>
          {slice.variation === 'productsWithButton' && (
            <div
              className={`${styles.products_button_wrapper} ${isVisibleStyles}`}
            >
              <Button
                text={slice.primary.button_text}
                link={slice.primary.button_link}
                variation="white"
              />
            </div>
          )}
        </div>
      ) : (
        <div className={`${styles.overlay} ${styles.phoneOverlay}`}>
          <Container>
            <div className={styles.container}>
              <div className={`${styles.content_wrapper}`}>
                <div className={`${styles.title_wrapper} ${isVisibleStyles}`}>
                  <PrismicRichText
                    field={slice.primary.title}
                    components={components(styles)}
                  />
                </div>
                <p className={`${styles.subtitle} ${isVisibleStyles}`}>
                  {slice.primary.subtitle}
                </p>
                <div className={`${styles.button_wrapper} ${isVisibleStyles}`}>
                  <Button
                    variation="white"
                    link={slice.primary.link}
                    text={slice.primary.link_text}
                    styles={styles.button}
                  />

                  {isDesktop && (
                    <GetOfferSafety text={slice.primary.safe_text} />
                  )}
                </div>
              </div>
              <div className={styles.video_wrapper}>
                <div className={`${styles.video_animate} ${isVisibleStyles}`}>
                  <Video
                    linkWebm="/static/video/GetOffer/get-loan-phone.webm"
                    link="/static/video/GetOffer/get-loan-phone.mp4"
                    isVisible={isVisible}
                    className={styles.video}
                  />
                </div>
                <GetOfferAdvantages
                  list={slice.primary.items}
                  isVisible={isVisible}
                />
                {isMobile && <GetOfferSafety text={slice.primary.safe_text} />}
              </div>
            </div>
          </Container>
        </div>
      )}
      {(slice.variation === 'withProducts' || onlyProducts) && (
        <div
          className={`${styles.overlay} ${styles.productsOverlay} ${slice.variation === 'withProducts' ? styles.withProductsOverlay : ''}`}
        >
          <Container>
            <GetOfferProducts
              products={slice.primary.products}
              variation={slice.variation}
            />
          </Container>
        </div>
      )}
    </div>
  );
};
